import {
  DependencyMapNodeViewModel,
  DependencyMapReference,
  DependencyMapReferenceOrParent,
  DependencyMapReferenceParent,
  DependencyMapReferenceViewModel,
  DependencyMapViewNodeProperties,
} from './types';
import { isEqual, isEqualWith } from 'lodash';
import { isViewReference } from './utils';
import { logError } from '@ardoq/logging';

type DependencyMapViewNodeCallbacks = Pick<
  DependencyMapViewNodeProperties,
  | 'onComponentClick'
  | 'onComponentDoubleClick'
  | 'onReferenceClick'
  | 'onUrlIconClick'
  | 'setHoveredComponentId'
  | 'setFocusedComponentId'
>;

export const haveSameCallbacks = (
  a: DependencyMapViewNodeCallbacks,
  b: DependencyMapViewNodeCallbacks
): boolean =>
  a.onComponentClick === b.onComponentClick &&
  a.onComponentDoubleClick === b.onComponentDoubleClick &&
  a.onReferenceClick === b.onReferenceClick &&
  a.onUrlIconClick === b.onUrlIconClick &&
  a.setHoveredComponentId === b.setHoveredComponentId &&
  a.setFocusedComponentId === b.setFocusedComponentId;

export const referenceEqualWith = (
  referenceA: DependencyMapReferenceOrParent | DependencyMapReferenceOrParent[],
  referenceB: DependencyMapReferenceOrParent | DependencyMapReferenceOrParent[]
) => {
  if (Array.isArray(referenceA) || Array.isArray(referenceB)) {
    return undefined;
  }
  if (referenceA.className !== referenceB.className) {
    return false;
  }
  if (isDependencyMapReference(referenceA)) {
    return (
      isDependencyMapReference(referenceB) &&
      isReferenceEffectivelyEqual(referenceA, referenceB)
    );
  }
  return (
    !isDependencyMapReference(referenceB) &&
    isNodeEffectivelyEqual(referenceA, referenceB)
  );
};

export const isNodeEffectivelyEqual = (
  a: DependencyMapNodeViewModel,
  b: DependencyMapNodeViewModel
) => {
  if (
    a.id !== b.id ||
    a.modelId !== b.modelId ||
    a.className !== b.className ||
    a.isComponent !== b.isComponent ||
    a.label !== b.label ||
    a.iconColor !== b.iconColor ||
    !isEqual(a.representationData, b.representationData) ||
    !isEqual(a.otherLabels, b.otherLabels)
  ) {
    return false;
  }
  if (
    [
      { aReferences: a.incomingReferences, bReferences: b.incomingReferences },
      { aReferences: a.outgoingReferences, bReferences: b.outgoingReferences },
      { aReferences: a.combinedReferences, bReferences: b.combinedReferences },
    ].some(
      ({ aReferences, bReferences }) =>
        (!aReferences && bReferences) ||
        (aReferences && !bReferences) ||
        (aReferences &&
          bReferences &&
          !isEqualWith(aReferences, bReferences, referenceEqualWith))
    )
  ) {
    return false;
  }
  if (
    (!a.children && b.children) ||
    (a.children && !b.children) ||
    (a.children &&
      b.children &&
      !isEqualWith(a.children, b.children, nodeEqualWith))
  ) {
    return false;
  }
  return true;
};

const isReferenceParentEffectivelyEqual = (
  a: DependencyMapReferenceParent | null,
  b: DependencyMapReferenceParent | null
): boolean => {
  if (a === null) {
    return b === null;
  }
  return (
    b !== null &&
    isNodeEffectivelyEqual(a, b) &&
    isReferenceParentEffectivelyEqual(a.parent, b.parent)
  );
};

const isReferenceEffectivelyEqual = (
  a: DependencyMapReferenceViewModel,
  b: DependencyMapReferenceViewModel
) =>
  a.label === b.label &&
  isNodeEffectivelyEqual(a.node, b.node) &&
  isReferenceParentEffectivelyEqual(a.parent, b.parent) &&
  isEqual(a.otherLabels, b.otherLabels);

export const nodeEqualWith = (
  nodeA:
    | DependencyMapNodeViewModel
    | DependencyMapReference
    | (DependencyMapNodeViewModel | DependencyMapReference)[],
  nodeB:
    | DependencyMapNodeViewModel
    | DependencyMapReference
    | (DependencyMapNodeViewModel | DependencyMapReference)[]
) => {
  if (Array.isArray(nodeA) || Array.isArray(nodeB)) {
    return undefined;
  }
  if (isViewReference(nodeA)) {
    return isViewReference(nodeB) && isReferenceEffectivelyEqual(nodeA, nodeB);
  }
  return !isViewReference(nodeB) && isNodeEffectivelyEqual(nodeA, nodeB);
};

export const hasComponentId = (
  children: (DependencyMapNodeViewModel | DependencyMapReferenceViewModel)[],
  componentId: string | null
): boolean =>
  componentId !== null &&
  children.some(child => {
    const node = isViewReference(child) ? child.node : child;

    return (
      node.modelId === componentId ||
      (node.children && hasComponentId(node.children, componentId)) ||
      nodeHasComponentIdInReferences(node, componentId)
    );
  });

export const isDependencyMapReference = (
  reference: DependencyMapReferenceOrParent
): reference is DependencyMapReferenceViewModel => 'node' in reference;

const referenceParentHasComponentId = (
  referenceParent: DependencyMapReferenceParent,
  componentId: string
): boolean =>
  referenceParent.modelId === componentId ||
  (referenceParent.parent !== null &&
    referenceParentHasComponentId(referenceParent.parent, componentId));

const referenceChildrenContainsComponentId = (
  references: DependencyMapReferenceOrParent[] | null,
  componentId: string
): boolean => {
  if (!references) {
    logError(
      Error('Inconsistent source data: References array is null or undefined'),
      null,
      { referencesIsNull: references === null }
    );
    return false;
  }
  return references.some(reference =>
    isDependencyMapReference(reference)
      ? reference.node.modelId === componentId
      : reference.modelId === componentId ||
        referenceChildrenContainsComponentId(reference.children, componentId)
  );
};

const referenceHasComponentId = (
  reference: DependencyMapReferenceOrParent,
  componentId: string
): boolean => {
  if (componentId === null) {
    return false;
  }
  if (isDependencyMapReference(reference)) {
    return (
      reference.node.modelId === componentId ||
      (reference.node.children &&
        hasComponentId(reference.node.children, componentId)) ||
      nodeHasComponentIdInReferences(reference.node, componentId) ||
      (reference.parent !== null &&
        referenceParentHasComponentId(reference.parent, componentId))
    );
  }

  return (
    reference.modelId === componentId ||
    referenceChildrenContainsComponentId(reference.children, componentId)
  );
};

export const nodeHasComponentIdInReferences = (
  node: DependencyMapNodeViewModel,
  componentId: string | null
) =>
  Boolean(
    componentId &&
    [
      node.incomingReferences,
      node.outgoingReferences,
      node.combinedReferences,
    ].some(
      references =>
        references &&
        references.some(reference =>
          referenceHasComponentId(reference, componentId)
        )
    )
  );
